export type GoogleSignInMode = "popup" | "redirect" | "blocked";
export type InAppBrowserSource =
  | "linkedin"
  | "telegram"
  | "instagram"
  | "facebook"
  | "tiktok"
  | "snapchat"
  | "pinterest"
  | "reddit"
  | "twitter"
  | "unknown";

type InAppBrowserDetection = {
  isInAppBrowser: boolean;
  sourceApp: InAppBrowserSource;
};

const IN_APP_BROWSER_PATTERNS: Array<{
  sourceApp: InAppBrowserSource;
  tokens: string[];
}> = [
  {
    sourceApp: "linkedin",
    tokens: ["linkedinapp", "linkedin"],
  },
  {
    sourceApp: "telegram",
    tokens: ["telegram", "telegrambot", "tgios", "tgandroid"],
  },
  {
    sourceApp: "instagram",
    tokens: ["instagram"],
  },
  {
    sourceApp: "facebook",
    tokens: ["fban", "fbav", "fb_iab", "fb4a", "fbios", "messenger"],
  },
  {
    sourceApp: "tiktok",
    tokens: ["tiktok", "musical_ly"],
  },
  {
    sourceApp: "snapchat",
    tokens: ["snapchat"],
  },
  {
    sourceApp: "pinterest",
    tokens: ["pinterest"],
  },
  {
    sourceApp: "reddit",
    tokens: ["reddit"],
  },
  {
    sourceApp: "twitter",
    tokens: ["twitter", "x-twitter"],
  },
];

const IN_APP_REFERRER_PATTERNS: Array<{
  sourceApp: InAppBrowserSource;
  tokens: string[];
}> = [
  {
    sourceApp: "linkedin",
    tokens: ["linkedin.com", "lnkd.in"],
  },
  {
    sourceApp: "telegram",
    tokens: ["t.me", "telegram.me"],
  },
  {
    sourceApp: "instagram",
    tokens: ["instagram.com", "l.instagram.com"],
  },
  {
    sourceApp: "facebook",
    tokens: ["facebook.com", "m.facebook.com", "lm.facebook.com", "messenger.com"],
  },
  {
    sourceApp: "tiktok",
    tokens: ["tiktok.com"],
  },
  {
    sourceApp: "snapchat",
    tokens: ["snapchat.com"],
  },
  {
    sourceApp: "pinterest",
    tokens: ["pinterest.com"],
  },
  {
    sourceApp: "reddit",
    tokens: ["reddit.com"],
  },
  {
    sourceApp: "twitter",
    tokens: ["x.com", "twitter.com", "t.co"],
  },
];

const IOS_ALT_BROWSER_TOKENS = ["crios", "fxios", "edgios", "opios"];

const normalize = (userAgent: string) => userAgent.toLowerCase();

const detectKnownInAppBrowserSource = (
  userAgent: string,
): InAppBrowserSource | null => {
  for (const entry of IN_APP_BROWSER_PATTERNS) {
    if (entry.tokens.some((token) => userAgent.includes(token))) {
      return entry.sourceApp;
    }
  }
  return null;
};

const detectInAppBrowserFromReferrer = (): InAppBrowserSource | null => {
  if (typeof document === "undefined") return null;
  const referrer = normalize(String(document.referrer || ""));
  if (!referrer) return null;

  for (const entry of IN_APP_REFERRER_PATTERNS) {
    if (entry.tokens.some((token) => referrer.includes(token))) {
      return entry.sourceApp;
    }
  }

  return null;
};

const detectInAppBrowserFromRuntime = (): InAppBrowserSource | null => {
  if (typeof window === "undefined") return null;
  const runtimeWindow = window as Window & {
    TelegramWebviewProxy?: unknown;
    Telegram?: { WebApp?: unknown };
    ReactNativeWebView?: unknown;
  };

  if (
    typeof runtimeWindow.TelegramWebviewProxy !== "undefined" ||
    runtimeWindow.Telegram?.WebApp
  ) {
    return "telegram";
  }

  if (typeof runtimeWindow.ReactNativeWebView !== "undefined") {
    return "unknown";
  }

  return null;
};

const isAndroidWebView = (userAgent: string): boolean => {
  if (!userAgent.includes("android")) return false;

  if (userAgent.includes("; wv") || userAgent.includes(" wv")) {
    return true;
  }

  // Most Android embedded WebViews include Version/4.0 before Chrome token.
  if (userAgent.includes("version/4.0")) {
    return true;
  }

  return userAgent.includes("webview");
};

const isIosInAppWebView = (userAgent: string): boolean => {
  const isIos = /iphone|ipad|ipod/.test(userAgent);
  if (!isIos) return false;

  const isAlternativeIosBrowser = IOS_ALT_BROWSER_TOKENS.some((token) =>
    userAgent.includes(token),
  );
  const isSafari = userAgent.includes("safari") && userAgent.includes("version/");
  const isStandardIosBrowser = isAlternativeIosBrowser || isSafari;

  return !isStandardIosBrowser;
};

export const detectInAppBrowser = (
  userAgent: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
): InAppBrowserDetection => {
  const runtimeDetectedSource = detectInAppBrowserFromRuntime();
  if (runtimeDetectedSource) {
    return { isInAppBrowser: true, sourceApp: runtimeDetectedSource };
  }

  const referrerDetectedSource = detectInAppBrowserFromReferrer();
  if (referrerDetectedSource) {
    return { isInAppBrowser: true, sourceApp: referrerDetectedSource };
  }

  if (!userAgent) {
    return { isInAppBrowser: false, sourceApp: "unknown" };
  }

  const ua = normalize(userAgent);
  const sourceApp = detectKnownInAppBrowserSource(ua);

  if (sourceApp) {
    return { isInAppBrowser: true, sourceApp };
  }

  if (isAndroidWebView(ua) || isIosInAppWebView(ua)) {
    return { isInAppBrowser: true, sourceApp: "unknown" };
  }

  return { isInAppBrowser: false, sourceApp: "unknown" };
};

export const isMobileBrowser = (userAgent: string): boolean => {
  const ua = normalize(userAgent);
  return /android|iphone|ipad|ipod|mobile/.test(ua);
};

export const isLikelyInAppBrowser = (userAgent: string): boolean => {
  return detectInAppBrowser(userAgent).isInAppBrowser;
};

export const resolveGoogleSignInMode = (
  userAgent: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
): GoogleSignInMode => {
  if (!userAgent) return "popup";
  if (isLikelyInAppBrowser(userAgent)) return "blocked";
  if (isMobileBrowser(userAgent)) {
    const configuredMode = String(
      import.meta.env.VITE_GOOGLE_MOBILE_AUTH_MODE || "",
    )
      .toLowerCase()
      .trim();
    if (configuredMode === "redirect") {
      return "redirect";
    }
    return "popup";
  }
  return "popup";
};

export const getGoogleAuthErrorMessage = (error: unknown): string => {
  const code = String((error as { code?: string })?.code || "").toLowerCase();
  const message = String((error as { message?: string })?.message || "").trim();

  if (code === "auth/disallowed-useragent" || message.includes("disallowed_useragent")) {
    return "Google sign-in is blocked in this in-app browser. Open this page in Safari or Chrome and try again.";
  }

  if (code === "auth/popup-blocked") {
    return "Google popup was blocked. Please allow popups or continue in Safari/Chrome.";
  }

  if (code === "auth/network-request-failed") {
    return "Network error during Google sign-in. Check your connection and try again.";
  }

  if (message.startsWith("Firebase:")) {
    return message
      .replace(/^Firebase:\s*/i, "")
      .replace(/\s*\(auth\/[^\)]+\)\.?/i, "")
      .trim();
  }

  return message || "Google sign-in failed. Please try again.";
};
